import React, { useState, useEffect } from 'react';

interface FilterArea {
  areaId: number;
  areaName: string;
  stateName: string;
}

interface FilterAreasSelectorProps {
  filterAreasData: FilterArea[];
  selectedAreas: string;
  onSelectionChange: (selectedIds: string) => void;
  resetTrigger?: number; // Add this to trigger reset from parent
}

const FilterAreasSelector: React.FC<FilterAreasSelectorProps> = ({
  filterAreasData,
  selectedAreas,
  onSelectionChange,
  resetTrigger
}) => {
  const [selectedAreaIds, setSelectedAreaIds] = useState<number[]>([]);
  const [stateAnySelected, setStateAnySelected] = useState<Record<string, boolean>>({});
  const [previousAreaIds, setPreviousAreaIds] = useState<number[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from selectedAreas prop (load saved data)
  useEffect(() => {
    if (selectedAreas && filterAreasData.length > 0 && !isInitialized) {
      const savedIds = selectedAreas.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      setSelectedAreaIds(savedIds);
      setIsInitialized(true);
    } else if (!selectedAreas && filterAreasData.length > 0 && !isInitialized) {
      const allStatesAny: Record<string, boolean> = {};
      Object.keys(groupedByState).forEach(stateName => {
        allStatesAny[stateName] = true;
      });
      setStateAnySelected(allStatesAny);
      
      const allAreaIds = filterAreasData.map(area => area.areaId);
      onSelectionChange(allAreaIds.join(','));
      setIsInitialized(true);
    }
  }, [selectedAreas, filterAreasData]);

  // Auto-select "Any" for all states when filterAreasData changes
  useEffect(() => {
    const currentAreaIds = filterAreasData.map(area => area.areaId);
    
    if (filterAreasData.length > 0 && isInitialized) {
      const newAreaIds = currentAreaIds.filter(id => !previousAreaIds.includes(id));
      
      if (newAreaIds.length > 0) {
        const allStatesAny: Record<string, boolean> = {};
        Object.keys(groupedByState).forEach(stateName => {
          allStatesAny[stateName] = true;
        });
        setStateAnySelected(allStatesAny);
        
        const allAreaIds = filterAreasData.map(area => area.areaId);
        onSelectionChange(allAreaIds.join(','));
      }
    }
    
    if (filterAreasData.length > 0) {
      setPreviousAreaIds(currentAreaIds);
    }
  }, [filterAreasData, isInitialized]);

  // Auto-select all when dropdown changes
  useEffect(() => {
    if (resetTrigger !== undefined && filterAreasData.length > 0) {
      setSelectedAreaIds([]);
      
      const allStatesAny: Record<string, boolean> = {};
      Object.keys(groupedByState).forEach(stateName => {
        allStatesAny[stateName] = true;
      });
      setStateAnySelected(allStatesAny);
      
      // Send all area IDs to backend
      const allAreaIds = filterAreasData.map(area => area.areaId);
      onSelectionChange(allAreaIds.join(','));
      setIsInitialized(true);
    }
  }, [resetTrigger]);

  // Group areas by state
  const groupedByState = filterAreasData.reduce((acc, area) => {
    if (!acc[area.stateName]) {
      acc[area.stateName] = [];
    }
    acc[area.stateName].push(area);
    return acc;
  }, {} as Record<string, FilterArea[]>);

  const handleAreaChange = (areaId: number, checked: boolean) => {
    const area = filterAreasData.find(a => a.areaId === areaId);
    const stateName = area?.stateName;
    
    if (checked) {
      // If "Any" is currently selected for this state, uncheck it and only select this area
      if (stateName && stateAnySelected[stateName]) {
        const stateAreaIds = groupedByState[stateName].map(area => area.areaId);
        // Get IDs from other states that have "Any" selected
        const otherStatesAnyIds: number[] = [];
        Object.entries(stateAnySelected).forEach(([state, isAny]) => {
          if (isAny && state !== stateName) {
            const ids = groupedByState[state].map(a => a.areaId);
            otherStatesAnyIds.push(...ids);
          }
        });
        // Combine: other states' Any IDs + other manually selected IDs + this clicked area
        const manuallySelected = selectedAreaIds.filter(id => !stateAreaIds.includes(id));
        const newSelectedIds = [...otherStatesAnyIds, ...manuallySelected, areaId];
        setSelectedAreaIds(newSelectedIds);
        setStateAnySelected(prev => ({ ...prev, [stateName]: false }));
        onSelectionChange(newSelectedIds.join(','));
      } else {
        const newSelectedIds = [...selectedAreaIds, areaId];
        setSelectedAreaIds(newSelectedIds);
        onSelectionChange(newSelectedIds.join(','));
      }
    } else {
      const newSelectedIds = selectedAreaIds.filter(id => id !== areaId);
      setSelectedAreaIds(newSelectedIds);
      if (stateName) {
        setStateAnySelected(prev => ({ ...prev, [stateName]: false }));
      }
      onSelectionChange(newSelectedIds.join(','));
    }
  };

  const handleSelectAllState = (stateName: string, checked: boolean) => {
    const stateAreaIds = groupedByState[stateName].map(area => area.areaId);
    
    if (checked) {
      setStateAnySelected(prev => ({ ...prev, [stateName]: true }));
      // Get all IDs from states with "Any" selected
      const allAnyStateIds: number[] = [];
      Object.entries(groupedByState).forEach(([state, areas]) => {
        if (state === stateName || stateAnySelected[state]) {
          allAnyStateIds.push(...areas.map(a => a.areaId));
        }
      });
      // Add manually selected areas from states without "Any"
      const manuallySelected = selectedAreaIds.filter(id => !stateAreaIds.includes(id));
      const allIds = [...allAnyStateIds, ...manuallySelected];
      onSelectionChange(allIds.join(','));
    } else {
      setStateAnySelected(prev => ({ ...prev, [stateName]: false }));
      // Remove all state areas
      const newSelectedIds = selectedAreaIds.filter(id => !stateAreaIds.includes(id));
      setSelectedAreaIds(newSelectedIds);
      onSelectionChange(newSelectedIds.join(','));
    }
  };

  const isStateFullySelected = (stateName: string) => {
    const stateAreaIds = groupedByState[stateName].map(area => area.areaId);
    return stateAreaIds.every(id => selectedAreaIds.includes(id));
  };

  const shouldShowAreaAsChecked = (areaId: number, stateName: string) => {
    // If state "Any" is selected, don't show individual areas as checked
    if (stateAnySelected[stateName]) {
      return false;
    }
    return selectedAreaIds.includes(areaId);
  };

  return (
    <div className="filter-areas-container" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
      {Object.entries(groupedByState).map(([stateName, areas]) => (
        <div key={stateName} className="state-column" style={{ minWidth: '200px', border: '1px solid #ddd', padding: '10px', borderRadius: '5px' }}>
          <div className="state-header" style={{ marginBottom: '10px', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#007bff' }}>{stateName}</h4>
            <label style={{ fontSize: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={stateAnySelected[stateName] || false}
                onChange={(e) => handleSelectAllState(stateName, e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              Any
            </label>
          </div>
          <div className="areas-list">
            {areas.map(area => (
              <label key={area.areaId} style={{ display: 'block', marginBottom: '5px', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={shouldShowAreaAsChecked(area.areaId, stateName)}
                  onChange={(e) => handleAreaChange(area.areaId, e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                {area.areaName}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FilterAreasSelector;